import React, { useState } from 'react'
import { DeleteOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { Button, Dropdown, Popconfirm, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { removeNote } from 'modules/domain/note/managers'
import type { Note } from 'modules/domain/note/types'

type STNoteDropdownActionsProps = {
  note: Note
  onEdit: (note: Note) => void
  onDeleteSuccess: (noteId: string) => void
}

const STNoteDropdownActions: React.FC<STNoteDropdownActionsProps> = ({ note, onEdit, onDeleteSuccess }) => {
  const { t } = useTranslation(['notes'])
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await removeNote(note.id)
      onDeleteSuccess(note.id)
      void message.success(t('notes:actions.deleteSuccess', 'Note deleted.'))
    } catch {
      void message.error(t('notes:actions.deleteFailed', 'Failed to delete note.'))
    } finally {
      setDeleting(false)
    }
  }

  const items: MenuProps['items'] = [
    {
      key: 'edit',
      onClick: () => onEdit(note),
      label: t('notes:form.edit', 'Edit note'),
      icon: <EditOutlined />,
    },
    {
      key: 'delete',
      label: (
        <Popconfirm
          title={t('notes:confirm.deleteTitle', 'Delete note?')}
          description={t('notes:confirm.deleteDescription', 'This action cannot be undone.')}
          onConfirm={handleDelete}
          okText={t('common:confirm.delete', 'Delete')}
          cancelText={t('common:confirm.cancel', 'Cancel')}
          okButtonProps={{ loading: deleting }}
        >
          <div onClick={(e) => e.stopPropagation()}>{t('notes:form.delete', 'Delete note')}</div>
        </Popconfirm>
      ),
      icon: <DeleteOutlined />,
      danger: true,
    },
  ]

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button
        type="text"
        icon={<MoreOutlined />}
        loading={deleting}
        onClick={(e) => e.stopPropagation()}
        aria-label={t('notes:list.moreOptions', 'More options')}
      />
    </Dropdown>
  )
}

export default STNoteDropdownActions
